'use client';
import styled from '@emotion/styled';
import { Box, Chip, Divider, Typography } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { PortableText } from '@portabletext/react';
import { format } from 'date-fns';
import Link from 'next/link';
import { ComponentProps } from 'react';

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
    min-height: 100vh;
  }
`;

const Heading = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 40px;
  line-height: 112.3%;
  align-items: center;
  color: #ffffff;
  margin-bottom: 10px;
  min-width: 409px;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 20px;
    margin-top: 10px;
    min-width: unset;
  }

  > .chevron {
    margin-left: -30px;
    opacity: 0;
    transition: all 0.3s ease-in-out;
  }

  /* opacity and margin make chevron visible for people with low movement settings */
  @media (prefers-reduced-motion: reduce) {
    > .chevron {
      opacity: 1;
      margin-left: 0;
    }
  }

  &:hover {
    > .chevron {
      opacity: 1;
      margin-left: 0;
      transition: all 0.3s ease-in-out;
    }
  }
`;

const List = styled.ul`
  list-style-type: none;
`;
List.defaultProps = {
  className: 'career-list',
};

const StyledDivider = styled(Divider)`
  width: 100%;
  margin-top: 50px;
  margin-bottom: 50px;
  background-color: white;
`;

interface Tag {
  tag: string;
  _updatedAt: string;
  _createdAt: string;
  _rev: string;
  _type: string;
  _id: string;
}

interface UpdatesProps {
  updates: {
    _id: string;
    _type: 'jobListing';
    _rev: string;
    _updatedAt: string;
    _createdAt: string;
    title: string;
    body: ComponentProps<typeof PortableText>['value'];
    tags?: Tag[];
  }[];
  tag?: Tag;
}

const Updates = (props: UpdatesProps) => {
  const { tag, updates } = props;
  return (
    <MainDiv>
      <Typography variant="h1" style={{ color: 'white', paddingBottom: 30 }}>
        Updates {tag && `for ${tag.tag}`}
      </Typography>
      <Typography style={{ color: 'white' }}>
        Updates from the Curiko governance team. Click the title to read more.
      </Typography>
      <StyledDivider />
      {updates?.length > 0 &&
        updates?.map(update => (
          <Box key={update._id} mb={8} id={`update-${update._id}`} width={'100%'}>
            <Link href={`/updates/${update._id}`} style={{ textDecoration: 'none' }}>
              <Heading variant="h2">
                <ChevronRight fontSize="large" className="chevron" />
                {`${update.title}`}
              </Heading>
            </Link>
            <Typography variant="body1" style={{ color: 'white', marginRight: 10 }}>
              Published {format(new Date(update._createdAt), 'MMMM dd, yyyy')}.{' '}
              <Link href={`/updates/${update._id}`} style={{ color: 'white' }}>
                Read more
              </Link>
            </Typography>
            <Box mb={4} mt={2}>
              {update.tags &&
                update.tags.map(tag => (
                  <Link key={tag._id} href={`/updates/tag/${tag._id}`}>
                    <Chip
                      label={tag.tag}
                      style={{ backgroundColor: '#F6DBEE', cursor: 'pointer', marginRight: 10 }}
                    />
                  </Link>
                ))}
            </Box>
          </Box>
        ))}
    </MainDiv>
  );
};

export default Updates;
